/**
 * Reshape a freshly parsed glTF scene graph into the node layout the Unity
 * client builds for the same file, so that Unity's `HierachyID` child-index
 * paths address the same objects on both clients.
 *
 * ## Why the two loaders disagree
 *
 * A glTF mesh may carry several primitives (one per material). The two
 * loaders turn that into different trees:
 *
 *  - **three `GLTFLoader`** makes the node a `Group` whose FIRST children are
 *    the primitives, in primitive order (`Mesh001`, `Mesh001_1`, …), followed
 *    by the node's real glTF children.
 *  - **glTFast** (Campfire's vendored copy, `GameObjectInstantiator.AddPrimitive`
 *    + `GltfImport.PopulateHierarchy`) instantiates the whole node hierarchy
 *    first and only then adds meshes: mesh numeration 0 lands ON the node
 *    GameObject, further numerations become child GameObjects named
 *    `{mesh}_{n}` appended AFTER the real children. Numeration order is
 *    glTFast's clustering: primitives with the same vertex layout
 *    (`VertexBufferDescriptor` — normals, tangents, UV-set count, colors,
 *    bones, morph-target count) merge into one Unity mesh with submeshes, in
 *    order of first appearance; Draco-compressed primitives never merge and
 *    are appended after the clusters. Campfire then splits submeshes
 *    (`GameObjectExtensions.SplitSubMeshes`, at import and lazily at load via
 *    `ProjectModelSlim.FixHierarchy`): submesh 0 stays on its GameObject and
 *    submeshes 1..k become children `Primitive_k`, appended last.
 *
 * Every part snapshot's `HierachyID` is authored against the glTFast shape, so
 * a plain child-index walk over three's shape is off by one primitive on every
 * multi-primitive node: each primitive inherits its neighbour's material and
 * the node's own material falls onto the leftover last child. That is how the
 * Optics `Mirror` plate rendered as a white mirror on the web while Unity drew
 * it in `Black Flat` (2026-09-11).
 *
 * ## What this does
 *
 * For each three `Group` that `GLTFLoader` synthesized from a multi-primitive
 * mesh (identified through `parser.associations`, never by name — CAD exports
 * routinely have real parts named `Bolt`, `Bolt_1`, …), the primitive that
 * glTFast puts on the node takes the group's place: it receives the group's
 * name, transform and `userData`, and is parented where the group was, at the
 * same sibling index. The remaining primitives and the real children are then
 * re-parented under it in glTFast's order. Afterwards the three tree IS the
 * Unity tree, so `HierachyID` walks, material application (a node's material
 * goes to the node's own mesh), picking, batching and the web import path
 * (which authors `HierachyID`s Unity will read back) all agree without special
 * cases.
 *
 * ## The scene layer (`TryToRemoveEmptyLayers`)
 *
 * glTFast (Campfire pins `SceneObjectCreation.Always`) instantiates every file
 * as `root/Scene/{root nodes}`, and `GLTFastImporter.TryToRemoveEmptyLayers`
 * — run on EVERY format and every `ImporterVersion`, at import and at load —
 * then removes wrapper layers by a purely structural test
 * (`CanRemoveLayer`: exactly one child, no `MeshFilter`, no
 * `SkinnedMeshRenderer`, no `Animation`): Campfire's own root always goes, and
 * the `Scene` layer goes iff the file has a single root node and no animation
 * (legacy clips put the `Animation` component on `Scene`). Only the removed
 * layer's SCALE is folded into the new root. three's `gltf.scene` is that
 * `Scene` layer, so {@link stripGltfSceneLayerLikeUnity} applies the same test
 * and re-points `gltf.scene` at the single root node. The web used to decide
 * this by matching node names against snapshot names, which both stripped
 * layers Unity keeps (an animated single-root file) and kept layers Unity
 * strips (names that did not happen to match) — the `RootNode0` drone that
 * rendered flat white was one of those.
 *
 * {@link normalizeGltfToUnityLayout} runs both steps in Unity's order. Must run
 * BEFORE `enforceTextureBudgetOnGltf` strips `gltf.parser`, and before the
 * asset is cached, cloned or walked for part authoring.
 */
import { Matrix4, Object3D, PropertyBinding } from "three";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";

interface GltfPrimitiveDef {
  readonly attributes?: Readonly<Record<string, number>>;
  readonly extensions?: Readonly<Record<string, unknown>>;
  readonly targets?: ReadonlyArray<unknown>;
}

interface GltfMeshDef {
  readonly name?: string;
  readonly primitives?: ReadonlyArray<GltfPrimitiveDef>;
}

interface GltfAssociation {
  readonly meshes?: number;
  readonly primitives?: number;
  readonly nodes?: number;
}

interface GltfParserLike {
  readonly json?: { readonly meshes?: ReadonlyArray<GltfMeshDef> };
  readonly associations?: Map<Object3D, GltfAssociation>;
}

export interface UnityLayoutReport {
  /** Multi-primitive nodes reshaped into the glTFast layout. */
  readonly restructured: number;
  /** Nodes left untouched, with the reason (logged by the caller if non-empty). */
  readonly skipped: ReadonlyArray<string>;
  /** Whether `gltf.scene` was re-pointed at the file's single root node. */
  readonly sceneLayerStripped: boolean;
}

const K_LAYOUT_MARK = "campfireUnityLayout";
const K_SCENE_LAYER_MARK = "campfireUnitySceneLayer";
const K_DRACO_EXTENSION = "KHR_draco_mesh_compression";
/** glTFast `GetTexCoordsCount` counts consecutive `TEXCOORD_i` from 0 and caps at 9. */
const K_MAX_TEXCOORD_SETS = 9;

/**
 * Reproduce glTFast's per-mesh "mesh assignments" for one glTF mesh: an
 * ordered list of primitive-index groups, each of which becomes ONE Unity mesh
 * (index 0 in the group is submesh 0, the rest are submeshes 1..k).
 *
 * Mirrors `GltfImport.LoadAccessorData`: non-Draco primitives cluster by
 * `VertexBufferDescriptor` in order of first appearance; Draco primitives are
 * single-primitive meshes appended after every cluster, in primitive order.
 */
export function computeGltfastMeshAssignments(
  primitives: ReadonlyArray<GltfPrimitiveDef>,
): number[][] {
  const clusters = new Map<string, number[]>();
  const singles: number[] = [];
  primitives.forEach((primitive, index) => {
    if (primitive.extensions && K_DRACO_EXTENSION in primitive.extensions) {
      singles.push(index);
      return;
    }
    const attributes = primitive.attributes ?? {};
    const has = (name: string): boolean => {
      const accessor = attributes[name];
      return typeof accessor === "number" && accessor >= 0;
    };
    let texCoordCount = 0;
    while (texCoordCount < K_MAX_TEXCOORD_SETS && has(`TEXCOORD_${texCoordCount}`)) {
      texCoordCount++;
    }
    const key = [
      has("NORMAL"),
      has("TANGENT"),
      texCoordCount,
      has("COLOR_0"),
      has("WEIGHTS_0") && has("JOINTS_0"),
      primitive.targets?.length ?? 0,
    ].join("|");
    const cluster = clusters.get(key);
    if (cluster) cluster.push(index);
    else clusters.set(key, [index]);
  });
  return [...clusters.values(), ...singles.map((index) => [index])];
}

/**
 * Apply every structural rule Unity applies to a loaded glTF, in Unity's
 * order: glTFast's node layout first, then `TryToRemoveEmptyLayers`.
 * Idempotent per GLTF result.
 */
export function normalizeGltfToUnityLayout(gltf: GLTF): UnityLayoutReport {
  const layout = restructureGltfToUnityLayout(gltf);
  const sceneLayerStripped = stripGltfSceneLayerLikeUnity(gltf);
  return { ...layout, sceneLayerStripped };
}

/**
 * Unity's `CanRemoveLayer` test on the `Scene` layer: a single root node and
 * no animation. Re-points `gltf.scene` at that node, folding the layer's
 * scale into it exactly as `TryToRemoveEmptyLayers` does (position and
 * rotation of a removed layer are dropped — three's scene group carries
 * identity for both, so nothing is lost). Idempotent.
 *
 * @returns `true` when the layer was removed.
 */
export function stripGltfSceneLayerLikeUnity(gltf: GLTF): boolean {
  const userData = gltf.userData as Record<string, unknown>;
  if (userData[K_SCENE_LAYER_MARK] === true) return false;
  userData[K_SCENE_LAYER_MARK] = true;
  const scene: Object3D = gltf.scene;
  if (scene.children.length !== 1) return false;
  if (gltf.animations.length > 0) return false;
  const root = scene.children[0]!;
  root.scale.multiply(scene.scale);
  scene.remove(root);
  root.updateMatrix();
  // `GLTF.scene` is typed as the loader's Group; after Unity's strip the
  // content root is whatever the single node is. Every consumer reads it as
  // an `Object3D`.
  (gltf as unknown as { scene: Object3D }).scene = root;
  return true;
}

/**
 * Reshape `gltf.scene` in place. Idempotent per GLTF result (marked on
 * `gltf.userData`), safe when the parser has already been stripped (no-op).
 */
export function restructureGltfToUnityLayout(gltf: GLTF): Omit<UnityLayoutReport, "sceneLayerStripped"> {
  const userData = gltf.userData as Record<string, unknown>;
  if (userData[K_LAYOUT_MARK] === true) return { restructured: 0, skipped: [] };
  const parser = (gltf as { parser?: GltfParserLike }).parser;
  const associations = parser?.associations;
  const meshDefs = parser?.json?.meshes;
  if (!associations || !meshDefs) {
    return { restructured: 0, skipped: ["parser state unavailable — layout left as loaded"] };
  }

  // Collect first: the reshaping edits the very children a traversal walks.
  const candidates: Array<{ group: Object3D; meshIndex: number }> = [];
  gltf.scene.traverse((object) => {
    const mapping = associations.get(object);
    if (!mapping || mapping.meshes === undefined || mapping.primitives !== undefined) return;
    if (mapping.nodes === undefined) return; // a mesh group that is not itself the node (node also carries a camera/light)
    candidates.push({ group: object, meshIndex: mapping.meshes });
  });

  const skipped: string[] = [];
  let restructured = 0;
  for (const { group, meshIndex } of candidates) {
    const reason = restructureGroup(group, meshIndex, associations, meshDefs[meshIndex]);
    if (reason) skipped.push(`${group.name || "<unnamed>"} (mesh ${meshIndex}): ${reason}`);
    else restructured++;
  }
  userData[K_LAYOUT_MARK] = true;
  return { restructured, skipped };
}

/** @returns a skip reason, or `null` when the group was reshaped. */
function restructureGroup(
  group: Object3D,
  meshIndex: number,
  associations: Map<Object3D, GltfAssociation>,
  meshDef: GltfMeshDef | undefined,
): string | null {
  const parent = group.parent;
  if (!parent) return "no parent";
  const primitiveDefs = meshDef?.primitives;
  if (!primitiveDefs) return "mesh definition missing";

  const primitives: Object3D[] = [];
  const realChildren: Object3D[] = [];
  for (const child of group.children) {
    const mapping = associations.get(child);
    if (mapping && mapping.meshes === meshIndex && mapping.primitives !== undefined) {
      primitives[mapping.primitives] = child;
    } else {
      realChildren.push(child);
    }
  }
  const primitiveCount = primitiveDefs.length;
  if (primitives.length !== primitiveCount || primitives.some((p) => p === undefined)) {
    return `expected ${primitiveCount} primitives, found ${primitives.filter(Boolean).length}`;
  }
  if (primitiveCount < 2) return null; // GLTFLoader never groups a single primitive

  const assignments = computeGltfastMeshAssignments(primitiveDefs);
  const own = primitives[assignments[0]![0]!]!;

  // The node's own mesh takes over the group's identity and pose. Primitive
  // meshes leave GLTFLoader with an identity local transform, so composing the
  // two matrices is the group's transform in every case the loader produces
  // and still correct should that ever change.
  const composed = new Matrix4().multiplyMatrices(group.matrix, own.matrix);
  composed.decompose(own.position, own.quaternion, own.scale);
  own.name = group.name || own.name;
  if (!group.name) own.uuid = group.uuid; // animation tracks bind by uuid when the node is unnamed
  own.userData = { ...own.userData, ...group.userData };
  own.visible = group.visible && own.visible;

  for (const child of [...group.children]) group.remove(child);

  // Same sibling slot: Unity child indices along the parent must not move.
  const slot = parent.children.indexOf(group);
  if (slot < 0) return "group not found among its parent's children";
  parent.children[slot] = own;
  own.parent = parent;
  group.parent = null;

  // Primitive names below follow glTFast (`{mesh}_{n}` per extra cluster,
  // `Primitive_k` per split submesh) so name-based fallbacks and web-authored
  // part names line up with what the desktop client shows. Known cost: three
  // binds morph-target WEIGHT tracks to primitive meshes by name, so a
  // multi-primitive mesh animated through weights would lose those tracks —
  // Campfire parts carry no morph animation, and node TRS tracks bind to the
  // node name, which `own` now carries.

  const baseName = PropertyBinding.sanitizeNodeName(meshDef?.name || "Primitive");
  for (const child of realChildren) own.add(child);
  for (let numeration = 1; numeration < assignments.length; numeration++) {
    const cluster = assignments[numeration]!;
    const head = primitives[cluster[0]!]!;
    head.name = `${baseName}_${numeration}`;
    own.add(head);
    for (let submesh = 1; submesh < cluster.length; submesh++) {
      const extra = primitives[cluster[submesh]!]!;
      extra.name = `Primitive_${submesh}`;
      head.add(extra);
    }
  }
  const ownCluster = assignments[0]!;
  for (let submesh = 1; submesh < ownCluster.length; submesh++) {
    const extra = primitives[ownCluster[submesh]!]!;
    extra.name = `Primitive_${submesh}`;
    own.add(extra);
  }
  own.updateMatrix();
  return null;
}
